import { useEffect, useMemo, useRef, useState } from 'react'
import arrowRight from '../../../../assets/icons/arrowRight.svg'
import { useDispatch, useSelector } from 'react-redux'
import { AppDispatch, RootState } from '../../../../redux'
import ivyLeagueSlice from '../../../../redux/slice/ivyLeague.slice'
import {
  IvyLeagueFamilyInfo,
  OccupationalDiscountType,
  PaymentDiscountType,
  PaymentMethod
} from '../../../../types/ivyLeague'

const PAYMENT_OPTIONS: { value: PaymentDiscountType; label: string }[] = [
  { value: 'AUGUST', label: 'August (pay-in-full)' },
  { value: 'OCTOBER', label: 'October (pay-in-full)' },
  { value: 'DECEMBER', label: 'December (pay-in-full)' },
  { value: 'FEBRUARY', label: 'February (pay-in-full)' },
  { value: 'MONTHLY', label: 'Monthly' },
  { value: 'INCENTIVE_CURRENT_STUDENT', label: 'Incentive (current student)' },
  { value: 'NONE', label: 'None' }
]

export const Step3 = () => {
  const dispatch: AppDispatch = useDispatch()
  const { familyInfo } = useSelector((state: RootState) => state.ivyLeague)
  const [openDropdown, setOpenDropdown] = useState<null | 'paymentPlan' | 'occupational' | 'paymentMethod'>(null)

  const paymentPlanOpen = openDropdown === 'paymentPlan'
  const occupationalOpen = openDropdown === 'occupational'
  const paymentMethodOpen = openDropdown === 'paymentMethod'

  type OccupationSelection = 'ActiveTeacher' | 'Military' | 'Other'

  const occupationOptions = useMemo(
    () => [
      { value: 'ActiveTeacher' as OccupationSelection, label: 'Active Teacher' },
      { value: 'Military' as OccupationSelection, label: 'Active or Retired Military' },
      { value: 'Other' as OccupationSelection, label: 'Other' }
    ],
    []
  )

  const selectedOccupation: OccupationSelection = familyInfo.isTeacherFamily
    ? 'ActiveTeacher'
    : (familyInfo.occupationalDiscountType === 'Military' ? 'Military' : 'Other')

  const paymentMethodOptions = useMemo(
    () => [
      { value: 'Other' as PaymentMethod, label: 'Check / ACH' },
      { value: 'CreditCard' as PaymentMethod, label: 'Credit Card' }
    ],
    []
  )

  const update = (updates: Partial<IvyLeagueFamilyInfo>) => {
    dispatch(ivyLeagueSlice.actions.setFamilyInfo({ ...familyInfo, ...updates }))
  }

  const setOccupation = (occupation: OccupationSelection) => {
    if (occupation === 'ActiveTeacher') {
      update({
        isTeacherFamily: true,
        occupationalDiscountType: 'None'
      })
      return
    }

    if (occupation === 'Military') {
      update({
        isTeacherFamily: false,
        occupationalDiscountType: 'Military' as OccupationalDiscountType
      })
      return
    }

    update({
      isTeacherFamily: false,
      occupationalDiscountType: 'None'
    })
  }

  const closeDropdown = () => setOpenDropdown(null)

  const dropdownContainerRefs = useRef<Map<string, HTMLDivElement>>(new Map())
  const setDropdownContainerRef = (key: string) => (el: HTMLDivElement | null) => {
    if (el) dropdownContainerRefs.current.set(key, el)
    else dropdownContainerRefs.current.delete(key)
  }

  useEffect(() => {
    if (!openDropdown) return
    const el = dropdownContainerRefs.current.get(openDropdown)
    if (!el) return
    el.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
  }, [openDropdown])

  return (
    <div className="step3">
      <div className="formArea">
        <div className="inputFieldArea">
          <label className="fieldLabel">Occupation</label>
          <div
            className="selectInputFieldArea"
            onClick={() => setOpenDropdown(occupationalOpen ? null : 'occupational')}
          >
            {occupationOptions.find((o) => o.value === selectedOccupation)?.label ?? 'Select...'}
            <img className="arrowIcon" src={arrowRight} alt="" />
          </div>
          {occupationalOpen && (
            <div className="dropdownContainer" ref={setDropdownContainerRef('occupational')}>
              {occupationOptions.map((opt) => (
                <div
                  className="dropdownOption"
                  key={opt.value}
                  onClick={() => {
                    setOccupation(opt.value)
                    closeDropdown()
                  }}
                >
                  {opt.label}
                </div>
              ))}
            </div>
          )}
          <label className="helpText">
            Teacher and military discounts do not stack.
          </label>
        </div>

        <div className="earlyPayDiscountArea" style={{ marginBottom: '20px' }}>
          <div className="checkboxArea ivyLeagueCheckbox">
            <input
              type="checkbox"
              checked={familyInfo.isAlumniFamily}
              className="checkbox"
              onChange={() => update({ isAlumniFamily: !familyInfo.isAlumniFamily })}
            />
            <label
              className="checkmark"
              onClick={() => update({ isAlumniFamily: !familyInfo.isAlumniFamily })}
            />
          </div>
          <label className="fieldLabel">Alumni family?</label>
        </div>

        <div className="earlyPayDiscountArea" style={{ marginBottom: '20px' }}>
          <div className="checkboxArea ivyLeagueCheckbox">
            <input
              type="checkbox"
              checked={familyInfo.hasReferralDiscount}
              className="checkbox"
              onChange={() => update({ hasReferralDiscount: !familyInfo.hasReferralDiscount })}
            />
            <label
              className="checkmark"
              onClick={() => update({ hasReferralDiscount: !familyInfo.hasReferralDiscount })}
            />
          </div>
          <label className="fieldLabel">Referral discount?</label>
        </div>

        <div className="inputFieldArea">
          <label className="fieldLabel">Payment plan</label>
          <div
            className="selectInputFieldArea"
            onClick={() => setOpenDropdown(paymentPlanOpen ? null : 'paymentPlan')}
          >
            {PAYMENT_OPTIONS.find((o) => o.value === familyInfo.paymentDiscountType)?.label ??
              'Select...'}
            <img className="arrowIcon" src={arrowRight} alt="" />
          </div>
          {paymentPlanOpen && (
            <div className="dropdownContainer" ref={setDropdownContainerRef('paymentPlan')}>
              {PAYMENT_OPTIONS.map((opt) => (
                <div
                  className="dropdownOption"
                  key={opt.value}
                  onClick={() => {
                    update({ paymentDiscountType: opt.value })
                    closeDropdown()
                  }}
                >
                  {opt.label}
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="inputFieldArea">
          <label className="fieldLabel">Payment method</label>
          <div
            className="selectInputFieldArea"
            onClick={() => setOpenDropdown(paymentMethodOpen ? null : 'paymentMethod')}
          >
            {paymentMethodOptions.find((o) => o.value === familyInfo.paymentMethod)?.label ?? 'Select...'}
            <img className="arrowIcon" src={arrowRight} alt="" />
          </div>
          {paymentMethodOpen && (
            <div className="dropdownContainer" ref={setDropdownContainerRef('paymentMethod')}>
              {paymentMethodOptions.map((opt) => (
                <div
                  className="dropdownOption"
                  key={opt.value}
                  onClick={() => {
                    update({ paymentMethod: opt.value })
                    closeDropdown()
                  }}
                >
                  {opt.label}
                </div>
              ))}
            </div>
          )}
          <label className="helpText">
            Credit card payments may incur a surcharge.
          </label>
        </div>
      </div>
      {openDropdown && (
        <div className="backDrop" onClick={closeDropdown} />
      )}
    </div>
  )
}
